import i18n from "../translations";
import { Article, HelpCenter, PublicLocale, IPositionable, WidgetMessengerEvent, HelpCenterMessengerEvent } from "../types";
import MobilityHelper from "./MobilityHelper";

class HelpCenterHelper {
  static POSITION_STEP_INDEX = 16384

  static getLocaleFromUrl(): PublicLocale | null {
    const pathSegments = window.location.pathname.split('/').filter(segment => segment.length > 0);

    if (pathSegments.length === 0) return null;

    // Check if first segment is a UUID (development scenario)
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

    let localeIndex = 0;
    if (pathSegments.length > 1 && uuidRegex.test(pathSegments[0])) {
      // Development URL: /uuid/locale/...
      localeIndex = 1;
    }

    const locale = pathSegments[localeIndex];

    // Check if the extracted locale is a valid PublicLocale
    if (locale && Object.values(PublicLocale).includes(locale as PublicLocale)) {
      return locale as PublicLocale;
    }

    return null;
  }

  static getArticleAuthorName(helpCenter: HelpCenter, article: Article) {
    if (article.author?.name?.length > 0) return article.author.name

    return helpCenter.business_name
  }

  static getLocaleTranslation(locale: string) {
    return i18n.t(`PublicLocale::${locale}`)
  }

  static getSupportedLocaleOptions(helpCenter?: HelpCenter) {
    if (!helpCenter) return []

    return helpCenter
      .supported_locales
      .map(locale => ({
        label: this.getLocaleTranslation(locale),
        value: locale
      }))
  }

  static getAvailableLocaleOptions() {
    return Object.keys(PublicLocale).map((locale) => ({
      label: this.getLocaleTranslation(PublicLocale[locale]),
      value: String(PublicLocale[locale]),
    }));
  }

  static getCreatePosition(resources: IPositionable[]): number {
    if (resources.length === 0) return this.POSITION_STEP_INDEX

    return resources[resources.length - 1].position + this.POSITION_STEP_INDEX
  }

  static getNewPositionBasedOnIndex<T extends IPositionable>(
    items: T[],
    index: number
  ) {
    const destinationIndex = index
    let destinationItem = items[destinationIndex];

    let destinationLeftNeighbourItem = items[destinationIndex - 1];
    let destinationRightNeighbourItem = items[destinationIndex];

    // Moved item to first position
    if (destinationItem && destinationIndex === 0) {
      return destinationItem.position / 2;
      // Moved item to last position
    } else if (destinationIndex === items.length) {
      destinationItem = items[destinationIndex - 1];

      return destinationItem.position + this.POSITION_STEP_INDEX;
      // Moved item to in between position
    } else if (destinationLeftNeighbourItem && destinationRightNeighbourItem) {
      // Moved item in between 2 items
      return (
        (destinationLeftNeighbourItem.position +
          destinationRightNeighbourItem.position) /
        2
      );
    }
  }

  static getArticleUrl(helpCenter: HelpCenter, article: Article, locale: PublicLocale, queryParams = {}) {
    if (!helpCenter || !article) return null

    return [
      helpCenter.url,
      locale,
      `articles/${article.id}-${MobilityHelper.getTranslation(locale, article.translations, 'slug') || 'draft'}`,
      queryParams ? `?${new URLSearchParams(queryParams).toString()}` : ''
    ]
      .join('/')
  }

  static isEmbedded() {
    const urlParams = new URLSearchParams(window.location.search)
    return Boolean(urlParams.get('embed')) === true
  }

  static sendParentMessage(message: HelpCenterMessengerEvent) {
    window?.parent?.parent?.postMessage(message, '*')
  }
}

export default HelpCenterHelper;